import React from 'react';
import { Card, Badge, Button, EmptyState } from '@tadagram/ui';
import { useMarketplaceEngine } from '../hooks/useMarketplaceEngine';
import { TradeStatus } from '../domain/marketplaceTypes';
import { ArrowLeftRight, ShoppingCart, Tag } from 'lucide-react';

const tradeStatusConfig: Record<TradeStatus, { label: string; variant: 'success' | 'warning' | 'danger' | 'neutral' }> = {
  escrow: { label: 'Escrow', variant: 'warning' },
  completed: { label: 'Hoàn tất', variant: 'success' },
  refunded: { label: 'Hoàn tiền', variant: 'danger' },
  disputed: { label: 'Đang tranh chấp', variant: 'danger' },
};

const TradeRow: React.FC<{ 
  trade: ReturnType<typeof useMarketplaceEngine>['trades'][0];
  onRelease: (id: string) => void;
  onDispute: (id: string) => void;
}> = ({ trade, onRelease, onDispute }) => {
  const { label, variant } = tradeStatusConfig[trade.status] ?? tradeStatusConfig.escrow;
  
  return (
    <Card className="p-4 flex gap-4 items-start hover:shadow-md transition-shadow">
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
          trade.side === 'buy' ? 'bg-emerald-100 text-emerald-600' : 'bg-indigo-100 text-indigo-600'
        }`}
      >
        {trade.side === 'buy' ? <ShoppingCart className="w-5 h-5" /> : <Tag className="w-5 h-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center gap-2 flex-wrap mb-1">
          <span className="font-bold text-sm text-slate-900 truncate">{trade.title}</span>
          <Badge variant={variant}>
            {label}
          </Badge>
        </div>
        <div className="flex gap-2.5 flex-wrap text-xs text-slate-500 items-center">
          <span>{trade.side === 'buy' ? `Người bán: ${trade.counterparty}` : `Người mua: ${trade.counterparty}`}</span>
          <span>·</span>
          <span className="text-blue-600 font-semibold">{trade.credits.toLocaleString('vi-VN')} Credits</span>
          <span>·</span>
          <span>Ref: {trade.orderRef}</span>
          <span>·</span>
          <span>{trade.time}</span>
        </div>
        
        {trade.status === 'escrow' && trade.side === 'buy' && (
          <div className="mt-3 flex gap-2">
            <Button 
              size="sm"
              onClick={() => onRelease(trade.id)}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              Đã nhận hàng (Giải phóng)
            </Button>
            <Button 
              size="sm"
              variant="danger"
              onClick={() => onDispute(trade.id)}
            >
              Báo cáo lỗi
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

const Trades: React.FC = () => {
  const { trades, isSyncing, releaseTradeFund, disputeTradeFund } = useMarketplaceEngine();
  const bought = trades.filter((t) => t.side === 'buy');
  const sold = trades.filter((t) => t.side === 'sell');

  const handleRelease = async (tradeId: string) => {
    if (confirm('Bạn xác nhận đã nhận hàng và muốn giải phóng tiền cho người bán?')) {
      const res = await releaseTradeFund(tradeId);
      alert(res.message);
    }
  };

  const handleDispute = async (tradeId: string) => {
    const reason = prompt('Vui lòng nhập lý do báo cáo lỗi (Tranh chấp):');
    if (reason && reason.trim()) {
      const res = await disputeTradeFund(tradeId, reason);
      alert(res.message);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {isSyncing && <p className="text-slate-500 text-sm m-0">Đang đồng bộ…</p>}

      {trades.length === 0 && !isSyncing && (
        <Card className="py-16 text-center text-slate-500 flex flex-col items-center justify-center">
          <ArrowLeftRight className="w-12 h-12 mb-3 opacity-40" />
          <p className="font-medium text-slate-700 m-0">Chưa có giao dịch nào</p>
        </Card>
      )}

      {bought.length > 0 && (
        <section>
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2.5">
            Đã mua ({bought.length})
          </h3>
          <div className="flex flex-col gap-2.5">
            {bought.map((t) => <TradeRow key={t.id} trade={t} onRelease={handleRelease} onDispute={handleDispute} />)}
          </div>
        </section>
      )}

      {sold.length > 0 && (
        <section>
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2.5">
            Đã bán ({sold.length})
          </h3>
          <div className="flex flex-col gap-2.5">
            {sold.map((t) => <TradeRow key={t.id} trade={t} onRelease={handleRelease} onDispute={handleDispute} />)}
          </div>
        </section>
      )}
    </div>
  );
};

export default Trades;
